import React, { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';

// react-pintura
import { PinturaEditor } from '@pqina/react-pintura';
import { Canvg } from 'canvg';
// pintura
import '@pqina/pintura/pintura.css';

import {
  // editor
  createDefaultImageReader,
  createDefaultImageWriter,
  createDefaultShapePreprocessor,

  // plugins
  setPlugins,
  createDefaultColorOptions,
  colorStringToColorArray,
  findNode,
  removeNode,
  getEditorDefaults
} from '@pqina/pintura';

import { useDispatch, useSelector } from 'react-redux';
import { lomavisCreatorRootState } from '../../../../pages/lomavisCreator';
import { useTranslate } from '@tolgee/react';
import { getLanguage } from '../../../../locales/i18n';
import {
  editImageThumbnail,
  saveEditorFullImage,
  updateThumbnailProcessing
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import { Button } from 'react-bootstrap';
import { getImageDimensions, scaleDownImage } from '../../utils/helpers';
import { EditorImageConfig } from '../../../../redux/slices/lomavisCreator/utils';

setPlugins(EditorImageConfig.plugins);

const getLocale = (lang) => {
  switch (lang) {
    case 'de':
    case 'de-DE':
      return {
        ...EditorImageConfig.DeLocale
      };
    case 'en':
    case 'en-GB':
    default:
      return {
        ...EditorImageConfig.EnLocale
      };
  }
};
const stringifyImageState = (imageState) => {
  return JSON.stringify(imageState, (k, v) => (v === undefined ? null : v));
};

const preprocessSVG = async (svgContent: string): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const v = Canvg.fromString(ctx, svgContent);
  await v.render();
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob as Blob));
  });
};

const handleSVGUpload = (file: File): Promise<Blob> => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = async (event) => {
      const svgString = event.target?.result as string;
      const rasterizedImage = await preprocessSVG(svgString); // Assuming preprocessSVG returns a Blob
      resolve(rasterizedImage);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });
};

export default function ImageEdit({
  platform,
  fileInputRef,
  setStartSubmission,
  handleClose,
  handleCloseNoConfirmation,
  triggerFullEditorImage
}) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [locale, setLocale] = useState(getLocale(getLanguage()));
  const editorRef = useRef(null);
  const editor2Ref = useRef(null);
  const handleEditorInit = (editor) => {
    editorRef.current = editor;
  };
  const layeredImageThumbnail = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].layeredImageThumbnail
  );
  const processingPreviewImage = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].thumbnail_preview_processing
  );
  const [addCrop, setAddCrop] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [storedImageState, setStoredImageState] = useState(
    '{"cropLimitToImage": false }'
  );
  useEffect(() => {
    setLocale(getLocale(getLanguage()));
  }, [getLanguage()]);
  const fullEditorImage = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].fullEditorImage
  );
  const editedImage = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].editedImageThumbnail
  );

  const editorDefaults = getEditorDefaults({
    ...EditorImageConfig.ImageEditorDefaults,

    locale
  });

  const preview_image = useSelector(
    (state: lomavisCreatorRootState) =>
      state.lomavisCreator.postData[platform].uploadedImageThumbnail.url
  );

  // inline
  const [result, setResult] = useState('');

  function removeObjectsByKeys(object, keysToRemove) {
    return Object.keys(object).reduce((result, key) => {
      if (!keysToRemove.includes(key)) {
        result[key] = object[key];
      }
      return result;
    }, {});
  }

  const getLastObject = (obj) => {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null; // or handle as needed
    }
    const lastKey = keys[keys.length - 1];
    return obj[lastKey];
  };

  const exportImage = () => {
    if (editorRef.current) {
      const editor = editorRef.current;
      if (updating) {
        return;
      }
      // Start timing
      const startTime = performance.now();

      // Dispatch update to indicate processing has started
      setUpdating(true);
      dispatch(
        updateThumbnailProcessing({
          state: true,
          platform: platform
        })
      );

      // Process the edited image
      editor.processImage().then((result) => {
        // Calculate the execution time when processing is done
        const endTime = performance.now();
        const executionTime = endTime - startTime;
        console.log(
          `exportImage execution time: ${executionTime.toFixed(2)} ms`
        );

        // Handle the resulting image data (base64 or blob)
        const editedImage = result.dest; // This will be a blob
        const url = URL.createObjectURL(editedImage);

        // Create a file object
        const file = new File([editedImage], 'image.jpg', {
          type: 'image/jpeg'
        });

        // Dispatch to update the thumbnail and stop processing indicator
        dispatch(
          editImageThumbnail({
            url: url,
            file: file,
            platform: platform
          })
        );
        dispatch(
          updateThumbnailProcessing({
            state: false,
            platform: platform
          })
        );
        setUpdating(false);

        console.log('edited image URL', url);
      });
    }
  };

  const debouncedExportImage = useCallback(
    debounce(() => {
      exportImage();
    }, 200),
    []
  );

  const handleEditorUpdate = async (imageState) => {
    const imageStateStr = stringifyImageState(imageState);
    setStoredImageState(imageStateStr);
    if (!addCrop && fullEditorImage.url && fullEditorImage.currentCrop) {
      setTimeout(async () => {
        let editsWithoutCrop = removeObjectsByKeys(imageState, ['crop']);
        const editedState = {
          ...editsWithoutCrop,
          crop: {
            ...fullEditorImage.currentCrop
          }
        };

        editorRef.current.history.write(editedState);
        setAddCrop(true);
      }, 400);
    }
    debouncedExportImage.cancel();
    debouncedExportImage();
  };
  const willRenderToolbar = (toolbar) => {
    // get the export button
    const exportButton = findNode('export', toolbar);

    removeNode('export', toolbar);

    return toolbar;
  };
  ///////////////////////////////
  const resetImageView = async () => {
    const editor = editorRef.current;

    // Utility function to get the last object in an array
    const getLastObject = (arr) => {
      if (!arr || arr.length === 0) return null;
      return arr[arr.length - 1];
    };
    const getFirstObject = (arr) => {
      if (!arr || arr.length === 0) return null;
      return arr[0];
    };

    // Utility function to remove specific keys from an object
    const removeObjectsByKeys = (obj, keys) => {
      const result = { ...obj };
      keys.forEach((key) => {
        delete result[key];
      });
      return result;
    };

    if (editor) {
      // Get the edited image state history
      const history = editor.history.get();
      const imageState = getLastObject(history);
      let OGWidth;
      let OGHeight;
      let HistoryWidth = getFirstObject(history)?.crop.width;
      let HistoryHeight = getFirstObject(history)?.crop.height;

      await getImageDimensions(
        fullEditorImage.url ? fullEditorImage.url : preview_image
      )
        .then((dimensions) => {
          OGHeight = dimensions?.height;
          OGWidth = dimensions?.width;
        })
        .catch((error) => {
          console.error('Error loading image:', error);
        });

      if (imageState) {
        // Remove crop-related keys from the image state
        let editsWithoutCrop = removeObjectsByKeys(imageState, ['crop']);

        const currentCrop = imageState.crop;
        const editedState = {
          ...editsWithoutCrop,
          crop: {
            height: OGHeight,
            width: OGWidth,
            x: 0,
            y: 0
          }
        };

        // Write the modified image state back to the editor's history
        editorRef.current.history.write(editedState);

        editorRef.current.processImage(editedState).then((result) => {
          // Handle the resulting image data (base64 or blob)
          const editedImage = result.dest; // This will be a blob

          const url = URL.createObjectURL(editedImage);

          // Do something with the URL, e.g., display it, download it, etc.

          dispatch(
            saveEditorFullImage({
              url: url,
              file: editedImage,
              platform: platform,
              currentCrop: currentCrop,
              uploadState: true
            })
          );

          setTimeout(() => {
            setStartSubmission(false);
            handleCloseNoConfirmation();
          }, 1000);
        });
      } else {
        console.log('No image state found in history.');
      }
    } else {
      console.log('Editor not initialized.');
    }
  };

  // const handleInputChange = async (e) => {
  //   const fileList = e.target.files; // This is a FileList
  //   const fileArray: File[] = Array.from(fileList); // Convert the FileList to an array

  //   const filePromises = fileArray.map(async (file: File) => {
  //     const cleanedFile = await handleSVGUpload(file);
  //     const previewImage = URL.createObjectURL(cleanedFile); // Assuming cleanedFile is a Blob

  //     setUploadedImage(previewImage);
  //     return cleanedFile;
  //   });

  //   // Wait for all files to be processed
  //   await Promise.all(filePromises);
  // };
  const handleInputChange = (e) => {
    // Exit if no files selected
    const fileList = e.target.files; // This is a FileList
    const fileArray = Array.from(fileList); // Convert the FileList to an array

    fileArray.map(async (file: any) => {
      let preview_image: any = URL.createObjectURL(file);
      let assetBlob: any = URL.createObjectURL(file);

      if (file) {
        try {
          const resizedBlob = await scaleDownImage(file);
          const url = URL.createObjectURL(resizedBlob);
          setUploadedImage(url);
        } catch (error) {
          console.error(error);
          setUploadedImage(preview_image);
        }
      }
    });

    fileInputRef.current.value = '';
  };

  const handleEditorProcess = (res) => {
    // 1. Turn imageState into string, replaces undefined values with null
    const imageStateStr = stringifyImageState(res.imageState);
    setStoredImageState(imageStateStr);
  };
  const handleImageUpdate = async () => {
    if (!uploadedImage || !storedImageState) {
      return;
    }

    function removeObjectsByKeys(object, keysToRemove) {
      return Object.keys(object).reduce((result, key) => {
        if (!keysToRemove.includes(key)) {
          result[key] = object[key];
        }
        return result;
      }, {});
    }

    const dimensions = await getImageDimensions(uploadedImage);

    const currentState = JSON.parse(storedImageState);
    const justAnnotations = [...currentState?.annotation];

    const filteredState = removeObjectsByKeys(currentState, ['annotation']);
    const newAnnote = {
      aspectRatio: 1,
      backgroundColor: [0, 0, 0, 0],
      backgroundImage: uploadedImage,
      backgroundSize: 'contain',
      cornerRadius: 0,
      disableErase: true,
      disableStyle: [
        'cornerRadius',
        'backgroundColor',
        'strokeColor',
        'strokeWidth'
      ],
      height: dimensions?.height,
      id: uploadedImage,
      isEditing: false,
      isSelected: true,
      opacity: 1,
      rotation: 0,
      strokeColor: [0, 0, 0],
      strokeWidth: 0,
      width: dimensions?.width,
      x: 0,
      y: 0
    };
    const combined = {
      ...filteredState,
      annotation: [...justAnnotations, newAnnote]
    };

    const imageStateStr = stringifyImageState(combined);
    editorRef.current.history.write(JSON.parse(imageStateStr));
    editorRef.current.util = undefined;
    editorRef.current.util = 'sticker';
  };

  useEffect(() => {
    if (layeredImageThumbnail.uploaded === true) {
      resetImageView();
    }
  }, [layeredImageThumbnail.uploaded, triggerFullEditorImage]);
  useEffect(() => {
    handleImageUpdate();
  }, [uploadedImage]);

  return (
    <div
      style={{ overflowY: 'scroll' }}
      className="thumbnail_Editor w-100 h-100 d-flex flex-column justify-content-center"
    >
      <div
        className="w-100"
        style={{ height: '80%', overflowY: 'auto', overflow: 'hidden' }}
      >
        <PinturaEditor
          {...editorDefaults}
          ref={editorRef}
          stickers={[
            ['Emoji', [...EditorImageConfig?.Stickers]],
            ['Markers', []]
          ]}
          cropSelectPresetOptions={[
            [9 / 16, '9:16'],
            [1, '1:1'],
            [4 / 5, '4:5'],
            [16 / 9, '16:9'],
            [undefined, t('lomavisCreator.editorCropRatio.Custom')]
          ]}
          src={fullEditorImage.url ? fullEditorImage.url : preview_image}
          onInit={handleEditorInit}
          onUpdate={handleEditorUpdate}
          enableZoom={false}
          enableZoomControls={false}
          cropInteractionFocus={'selection'}
          willRenderToolbar={willRenderToolbar}
          stickerStickToImage={true}
          onProcess={handleEditorProcess}
        ></PinturaEditor>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleInputChange}
        max={1}
        style={{ display: 'none' }}
      />
    </div>
  );
}
